import React, { useEffect, useState } from 'react';
import { Typography, Button } from '@mui/material';
import { useIntl, FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { get } from 'lodash';
import { useNotifications } from '@toolpad/core/useNotifications';

import { publishUnpublishPost } from '../../../Services/Api';
import queriesConfig from '../../../Services/queriesConfig';
import Dialog from '../../DesignSystem/Dialog';
import ButtonWithLoader from '../../ButtonWithLoader/ButtonWithLoader';
import { isPostPublished, isStudy } from '../../../Utils/Post';
import ParentWarning from './ParentWarning';
import QuestionMissingWarning from './QuestionsMissingWarning';
import { notify } from '../../../Utils/NotificationUtils';

const i18nKeys = (action: any) => {
  if (action === 'unpublish') {
    return {
      toast: 'PUBLISHING_POST_DIALOG.UNPUBLISH.TOAST',
      button: 'PUBLISHING_POST_DIALOG.UNPUBLISH.BUTTON',
      content: 'PUBLISHING_POST_DIALOG.UNPUBLISH.CONTENT',
      children_warning: 'PUBLISHING_POST_DIALOG.UNPUBLISH.CHILDREN_WARNING',
      no: 'PUBLISHING_POST_DIALOG.UNPUBLISH.NO',
      yes: 'PUBLISHING_POST_DIALOG.UNPUBLISH.YES',
    };
  }

  if (action === 'schedule') {
    return {
      toast: 'PUBLISHING_POST_DIALOG.SCHEDULE.TOAST',
      button: 'PUBLISHING_POST_DIALOG.SCHEDULE.BUTTON',
      content: 'PUBLISHING_POST_DIALOG.SCHEDULE.CONTENT',
      no: 'PUBLISHING_POST_DIALOG.SCHEDULE.NO',
      yes: 'PUBLISHING_POST_DIALOG.SCHEDULE.YES',
    };
  }

  if (action === 'unschedule') {
    return {
      toast: 'PUBLISHING_POST_DIALOG.UNSCHEDULE.TOAST',
      button: 'PUBLISHING_POST_DIALOG.UNSCHEDULE.BUTTON',
      content: 'PUBLISHING_POST_DIALOG.UNSCHEDULE.CONTENT',
      no: 'PUBLISHING_POST_DIALOG.UNSCHEDULE.NO',
      yes: 'PUBLISHING_POST_DIALOG.UNSCHEDULE.YES',
    };
  }

  return {
    toast: 'PUBLISHING_POST_DIALOG.PUBLISH.TOAST',
    button: 'PUBLISHING_POST_DIALOG.PUBLISH.BUTTON',
    content: 'PUBLISHING_POST_DIALOG.PUBLISH.CONTENT',
    no: 'PUBLISHING_POST_DIALOG.PUBLISH.NO',
    yes: 'PUBLISHING_POST_DIALOG.PUBLISH.YES',
  };
};

function PublishingPostDialog({
  publishedAt,
  open,
  onClose,
  action = 'publish',
  redirectTo,
  post,
}: any) {
  const postId = post?.id;
  const intl = useIntl();
  const navigate = useNavigate();
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const childrenCount = get(post, 'post_records_count');
  const displayParentWarning =
    post?.parent_id &&
    !isPostPublished(get(post, 'included.source')) &&
    ['publish', 'schedule'].includes(action);
  const noQuestions =
    isStudy(post?.type) && post?.form_builder && get(post, 'form_questions', []).length === 0;
  const [isLoading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    const shouldPublish = action === 'publish'; // unpublishing also clears the askToPublishAt

    const response = await publishUnpublishPost(postId, !shouldPublish);
    setLoading(false);

    if (response.ok) {
      onClose();
      queryClient.invalidateQueries(queriesConfig.getPost(postId).queryKey);
      queryClient.invalidateQueries(['dashboard-posts']);
      notify(notifications, intl.formatMessage({ id: i18nKeys(action).toast }));

      if (redirectTo) {
        navigate(redirectTo);
      }
    }
  };

  useEffect(() => {
    if (open) {
      onClose();
    }
  }, [publishedAt]);

  const dialogTitle = intl.formatMessage({ id: i18nKeys(action).button });
  const dialogActions = (
    <>
      <Button variant="outlined" size="large" onClick={onClose} color="primary">
        <FormattedMessage id={i18nKeys(action).no} />
      </Button>
      <ButtonWithLoader
        variant="contained"
        size="large"
        onClick={handleSubmit}
        color="primary"
        isFetching={isLoading}
        disabled={isLoading || displayParentWarning || noQuestions}
      >
        <FormattedMessage id={i18nKeys(action).yes} />
      </ButtonWithLoader>
    </>
  );

  return (
    <Dialog
      id={`publish-post-dialog-${postId}`}
      open={open}
      title={dialogTitle}
      label={dialogTitle}
      actions={dialogActions}
      onClose={onClose}
      variant="small"
    >
      <Typography variant="body1">
        {childrenCount > 0 && action === 'unpublish' && (
          <FormattedMessage
            id={i18nKeys(action).children_warning}
            values={{
              childrenCount: intl.formatMessage({ id: 'CHILDREN_COUNT' }, { count: childrenCount }),
            }}
          />
        )}{' '}
        {displayParentWarning && <ParentWarning post={post} />}
        {noQuestions && <QuestionMissingWarning post={post} onClose={onClose} />}
        {!noQuestions && !displayParentWarning && (
          <FormattedMessage id={i18nKeys(action).content} />
        )}
      </Typography>
    </Dialog>
  );
}

export default PublishingPostDialog;
